import RangePicker from '@home/components/RangePicker';
import { DBO_TM } from '@op/utils/format';
import moment, { Moment } from 'moment';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Bubble } from 'tea-component';
import { connect, useSelector } from 'umi';
import {
  moreKeyCommonMapStateToProps,
  moreKeyCommonMapDispatchToProps,
} from '../../connect/moreKeyCommon';
import {
  IDateCommonSelectType,
  IDateConfig,
  ISingleDateCommonType,
} from '../../types';
import useIntls from '@opm/hooks/useIntls';
import { dayToDaily, dailyToDay } from '@op/components/FilterCommon/constants/date';
import { getTimeLimitation, isTimeLimitation } from '@op/components/FilterCommon/utils';
import useIntersectionObserver from '@/pages/opinion/pages/Summary/hooks/useIntersectionObserver';
import classNames from 'classnames';
import './index.scss';
import BubbleCommon from '@op/components/BubbleCommon';
import { CONTACT_NAME } from '@op/constants';

const LONG_DATE_TIPS =
  `Please select the time range within 3 year. Feel free to contact {contact} if needed`;

export type TMultiDateSelect = IDateCommonSelectType & IDateConfig;



function formatTimeRangeValue(value: [Moment, Moment]): string[] {
  return value.map(item => item.format('YYYY-MM-DD HH:mm:ss'));
}

function MultiDateSelect({
  filterValue,
  valueKeys,
  change,
  optionsMap = { date: { value: 'date', text: 'Day', text_cn: '日' } },
  defaultValue = { date: [DBO_TM.utc(), DBO_TM.utc()] },
  type: propType,
  typeList = ['date'],
  startOfTime = '2001-01-01',
  disabledDate,
  className,
  tips,
  placement,
  showIcon,
  enableMaximumDuration = false,
  noLimitation = false,
  maximumDuration,
  ...props
}: TMultiDateSelect & ISingleDateCommonType) {
  const type = useMemo(() => {
    const dateTypeValue = filterValue?.[valueKeys.date_type];
    if (dateTypeValue && dailyToDay[dateTypeValue]) {
      return dailyToDay[dateTypeValue];
    }
    return propType || 'date';
  }, [filterValue, valueKeys.date_type, propType]);

  const { isEn, intl } = useIntls();
  const { userInfo } = useSelector((state: any) => state.home?.userInfo || {});
  const [showError, setShowError] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const { show } = useIntersectionObserver({
    target: ref.current,
    threshold: 1,
  });

  const maximumDurationValue = useMemo(
    () =>
      maximumDuration || {
        defaultNum: 3,
        defaultUnit: 'y' as const,
        tipsKey: LONG_DATE_TIPS,
        tipsValues: { contact: CONTACT_NAME },
      },
    [maximumDuration],
  );

  const rangePickerValue = useMemo(() => {
    const { start_time, end_time } = valueKeys;
    return [start_time, end_time].map(key => moment(filterValue[key]));
  }, [filterValue, valueKeys]);

  const checkTimeLimitation = (value: [Moment, Moment]): boolean => {
    if (!enableMaximumDuration) return false;
    if (!isTimeLimitation(noLimitation, userInfo)) return false;
    const { defaultNum, defaultUnit } = maximumDurationValue;
    return getTimeLimitation(
      formatTimeRangeValue(value),
      defaultNum,
      defaultUnit,
    );
  };

  useEffect(() => {
    if (!enableMaximumDuration) {
      setShowError(false);
      if (Boolean(filterValue.timeError)) {
        change({ timeError: false });
      }
      return;
    }

    const [startTime, endTime] = rangePickerValue;
    if (!startTime?.isValid?.() || !endTime?.isValid?.()) {
      setShowError(false);
      if (Boolean(filterValue.timeError)) {
        change({ timeError: false });
      }
      return;
    }

    const isFlag = checkTimeLimitation(rangePickerValue as [Moment, Moment]);
    setShowError(isFlag);
    if (Boolean(filterValue.timeError) !== isFlag) {
      change({ timeError: isFlag });
    }
  }, [
    enableMaximumDuration,
    rangePickerValue,
    userInfo,
    maximumDurationValue,
    noLimitation,
  ]);

  const showErrorInfo = useMemo(() => {
    if (!enableMaximumDuration || !showError || !show) return null;
    const { tipsKey, tipsValues } = maximumDurationValue;
    return intl(tipsKey, tipsValues);
  }, [enableMaximumDuration, showError, show, maximumDurationValue, intl]);

  const onChangeRangePicker = (value: [Moment, Moment], type?: string) => {
    const [start_time, end_time] = value || [];
    if (!start_time || !end_time) return;

    const momentUnit: moment.unitOfTime.StartOf = (() => {
      if (type === 'week') return 'week';
      if (type === 'month') return 'month';
      if (type === 'hour') return 'hour';
      return 'day';
    })();

    const { startTime, endTime } = {
      startTime: start_time
        ?.startOf(momentUnit)
        ?.startOf('hour')
        ?.format('YYYY-MM-DD HH:mm:ss'),
      endTime: end_time
        ?.endOf(momentUnit)
        ?.endOf('hour')
        ?.format('YYYY-MM-DD HH:mm:ss'),
    };

    const dateType = type ? dayToDaily[type] || 'daily' : 'daily';
    const isFlag = checkTimeLimitation(value);

    change({
      [valueKeys.start_time]: startTime,
      [valueKeys.end_time]: endTime,
      [valueKeys.date_type]: dateType,
      ...(enableMaximumDuration ? { timeError: isFlag } : {}),
    });
  };

  const content = (
    <div
      className={classNames(
        'date-select-wrapper',
        'search-item',
        'search-item-date',
        'date-select-wrapper-hour',
        className,
      )}
    >
      <div
        ref={ref}
        className={classNames('time-picker-wrapper', {
          'time-picker-wrapper-error': showError,
        })}
      >
        <RangePicker
          autoStartDay={false}
          onChange={onChangeRangePicker}
          typeList={typeList}
          type={type}
          startOfTime={startOfTime}
          defaultValue={defaultValue}
          startOfWeek={1}
          optionsMap={optionsMap}
          showSelectType={typeList.length > 1}
          value={rangePickerValue}
          disables={false}
          language={isEn ? 'en' : 'cn'}
          disabledDate={disabledDate}
          {...props}
        />
      </div>
    </div>
  );

  function getWrappedContent() {
    if (!tips) return content;
    const realShowIcon = showIcon ?? !!tips;
    const tipsMapInfo = showError && !realShowIcon ? null : tips;
    return (<BubbleCommon tipsMap={tipsMapInfo} showIcon={realShowIcon} placement={placement}>
      {content}
    </BubbleCommon>);
  }

  if (!enableMaximumDuration) {
    return getWrappedContent();
  }

  return (
    <Bubble
      visible={show && showError}
      error
      content={showErrorInfo}
    >
      {getWrappedContent()}
    </Bubble>
  );
}

export default connect(
  moreKeyCommonMapStateToProps,
  moreKeyCommonMapDispatchToProps,
)(MultiDateSelect);
